if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
    }

    hide(preventFocus = false) {
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);

      // Clean up any size chart overlays injected by the product page script
      document.querySelectorAll('[id^="rix-sizechart-quickadd-"]').forEach(el => el.remove());
      document.body.style.overflow = '';

      this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    show(opener) {
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      fetch(opener.getAttribute('data-product-url'))
        .then((response) => response.text())
        .then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
          this.reinitInventoryBadge();
          super.show(opener);
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    preventVariantURLSwitching() {
      this.modalContent.querySelector('variant-radios,variant-selects').setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      // Remove size chart overlays to prevent ID clashes with the main product page
      this.productElement.querySelectorAll('[id^="rix-sizechart-"]').forEach(el => el.remove());

      // Remove popup modals (size chart, etc) — their modal-dialog custom element won't init
      // inside quick add and the content renders visibly
      this.productElement.querySelectorAll('modal-dialog').forEach(el => el.remove());
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios').forEach((variantSelect) => {
        variantSelect.dataset.originalSection = sectionId;
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    reinitInventoryBadge() {
      // The product page scripts use data-section="X" but quick add prefixes it to "quickadd-X"
      // Find the variant-radios/selects and badge inside the modal and wire them up
      const modal = this.modalContent;
      const badge = modal.querySelector('[id^="inventory-"]');
      if (!badge) return;

      // Extract inv data embedded as a JSON-like object in the inline script — skip, use DOM instead
      // Build inv map from variant JSON embedded in variant-radios or variant-selects
      const jsonEl = modal.querySelector('variant-radios [type="application/json"], variant-selects [type="application/json"]');
      if (!jsonEl) return;

      let variants;
      try { variants = JSON.parse(jsonEl.textContent); } catch(e) { return; }

      const inv = {};
      variants.forEach(v => {
        inv[v.id] = {
          qty: v.inventory_quantity || 0,
          policy: v.inventory_policy,
          management: v.inventory_management,
          available: v.available
        };
      });

      const IS_DROPSHIP = badge.getAttribute('data-dropship') === 'true';

      function getBadgeHTML(d) {
        if (d.qty >= 5) return '<div class="inventory-badge inventory-badge--ready">🔥 <strong>Ready to Ship</strong> — Selling fast!</div>';
        if (d.qty > 0) return '<div class="inventory-badge inventory-badge--low">🔥 <strong>Ready to Ship</strong> — Only ' + d.qty + ' left in stock!</div>';
        if (d.available === false) return '<div class="inventory-badge inventory-badge--outofstock">❌ <strong>Out of Stock</strong></div>';
        if (IS_DROPSHIP) return '<div class="inventory-badge inventory-badge--thirdparty">💻 <strong>Ready to Ship</strong> — Dispatched from partner warehouse</div>';
        if (d.policy === 'continue') return '<div class="inventory-badge inventory-badge--preorder">' + (badge.getAttribute('data-preorder-html') || '✈️ <strong>Pre-order</strong> — Ships in an extra 5–9 working days') + '</div>'; /* 9 Oct 2026: the product's own wording (7-14 day brands, dated launches), set on the notice box by sections/main-product.liquid */
        return '';
      }

      function updateBadge(variantId) {
        const d = inv[variantId];
        if (d) badge.innerHTML = getBadgeHTML(d);
      }

      // Show badge for current selected variant immediately
      const radios = modal.querySelectorAll('variant-radios input[type="radio"]:checked');
      if (radios.length) {
        const options = Array.from(modal.querySelectorAll('variant-radios fieldset')).map(fs => {
          const c = fs.querySelector('input[type="radio"]:checked');
          return c ? c.value : null;
        }).filter(Boolean);
        const matched = variants.find(v => v.options.every((opt, i) => opt === options[i]));
        if (matched) updateBadge(matched.id);
      }

      // Listen for changes
      modal.querySelectorAll('variant-radios input[type="radio"]').forEach(radio => {
        radio.addEventListener('change', () => {
          const options = Array.from(modal.querySelectorAll('variant-radios fieldset')).map(fs => {
            const c = fs.querySelector('input[type="radio"]:checked');
            return c ? c.value : null;
          }).filter(Boolean);
          const matched = variants.find(v => v.options.every((opt, i) => opt === options[i]));
          if (matched) updateBadge(matched.id);
        });
      });

      modal.querySelectorAll('variant-selects select').forEach(select => {
        select.addEventListener('change', () => {
          const options = Array.from(modal.querySelectorAll('variant-selects select')).map(s => s.value);
          const matched = variants.find(v => v.options.every((opt, i) => opt === options[i]));
          if (matched) updateBadge(matched.id);
        });
      });
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
      
      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }
  });
}
